import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Spin, Space, Modal, message, Popconfirm } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, gql, useLazyQuery, useMutation } from '@apollo/client';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import DataGrid from '../../components/DataGrid';
import * as Config from '../../appConfig';
import { activePeriod, disabledDate } from '../../utils/date';

import {
  updateTableMetaData,
  updateGridMetaData,
  resetGridFilters,
} from '../../store/reducers/payrollSlice';
import { PAYMENT_LIST_FIELDS } from '../../apollo/fragments';
import {
  ADD_PAYMENT_MUTATION,
  UPDATE_PAYMENT_MUTATION,
  DELETE_PAYMENT_MUTATION,
  IMPORT_PAYMENTS_MUTATION,
} from '../../apollo/mutations';
import { EMPLOYEE_LOOKUP_QUERY } from '../../apollo/queries';

import ProfileCard from '../../components/Employee/ProfileCard';
import AddPaymentForm from '../../components/Payroll/Forms/AddPaymentForm';
import AddPaymentImportForm from '../../components/Payroll/Forms/AddPaymentImportForm';
import UploadFile from '../UploadFile';

const PAYMENTS_QUERY = gql`
  ${PAYMENT_LIST_FIELDS}
  query GetPayments(
    $first: Int
    $page: Int
    $filters: [QueryGetPaymentsFilter]
    $sort: QueryGetPaymentsSorter
  ) {
    payments(first: $first, page: $page, filters: $filters, sort: $sort) {
      paginatorInfo {
        count
        currentPage
        total
      }
      data {
        ...PaymentListFields
      }
    }
  }
`;

const Payments = ({ gridId }) => {
  const { metaData, grids } = useSelector((state) => state.payroll);
  const {
    user: { ability },
  } = useSelector((state) => state.login);
  const dispatch = useDispatch();

  const grid = grids.find((i) => i.id === gridId);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showAddEditPaymentModal, setShowAddEditPaymentModal] = useState(false);
  const [activePayment, setActivePayment] = useState(null);

  const [freezePrevMonth, currentMonth, prevMonth] = activePeriod();

  const [showAddPaymentImportModal, setShowAddPaymentImportModal] = useState(
    false
  );
  const [fileToImport, setFileToImport] = useState(null);
  const [importErrors, setImportErrors] = useState('');

  const queryVariables = useMemo(() => {
    return {
      first: grid.metaData.pagination.pageSize,
      page: grid.metaData.pagination.page,
      filters: grid.metaData.filters,
      sort: grid.metaData.sorting,
    };
  }, [
    grid.metaData.pagination.pageSize,
    grid.metaData.pagination.page,
    grid.metaData.filters,
    grid.metaData.sorting,
  ]);

  const { loading, data, error, refetch } = useQuery(PAYMENTS_QUERY, {
    variables: queryVariables,
  });

  console.log('ERROR', error);

  const [
    lookupEmployee,
    { data: lookupData, loading: fetching },
  ] = useLazyQuery(EMPLOYEE_LOOKUP_QUERY);

  const [addPayment, { loading: adding }] = useMutation(ADD_PAYMENT_MUTATION);
  const [updatePayment, { loading: updating }] = useMutation(
    UPDATE_PAYMENT_MUTATION
  );
  const [deletePayment, { loading: deleting }] = useMutation(
    DELETE_PAYMENT_MUTATION
  );

  const [importPayments, { loading: importing }] = useMutation(
    IMPORT_PAYMENTS_MUTATION
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  const onEmployeeLookup = (search) => {
    lookupEmployee({
      variables: {
        search,
        status: 'active',
      },
    });
  };

  const onSuccessMutation = (mesageText = 'Details updated successfully!') => {
    refetch(queryVariables);
    message.success(mesageText);
  };

  const columns = [
    {
      title: 'Sl.No',
      dataIndex: 'id',
      key: 'id',
      serverKey: 'ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      className: 'width-100',
    },
    {
      title: 'EMP ID',
      dataIndex: ['employee', 'emp_id'],
      key: 'emp_id',
      serverKey: 'EMP_ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      render: (value, record) => {
        return <ProfileCard employee={record.employee} />;
      },
    },
    {
      title: 'For Month',
      dataIndex: 'for_month',
      key: 'for_month',
      serverKey: 'MONTH',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'date',
      format: 'month',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      serverKey: 'AMOUNT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      format: 'currency',
      className: 'cell-currency',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      serverKey: 'NOTE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Created By',
      dataIndex: ['createdBy', 'name'],
      key: 'created_by',
      serverKey: 'USER',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'string',
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      serverKey: 'CREATED_AT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'date',
      format: 'date',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      serverKey: 'ACTIONS',
      sorter: false,
      canExclude: false,
      visible: true,
      ellipsis: false,
      disableSearch: true,
      className: 'width-75',
      render: (value, record) => {
        const for_month = moment(record.for_month);
        const isEditable = disabledDate(
          for_month,
          false,
          currentMonth,
          prevMonth
        );
        return (
          <Space>
            {ability.toEditPayment && (
              <Button
                type="default"
                size="small"
                icon={<EditOutlined />}
                title="Edit"
                onClick={() => {
                  setActivePayment(record);
                  setShowAddEditPaymentModal(true);
                }}
                disabled={isEditable}
              ></Button>
            )}
            {ability.toDeletePayment && (
              <Popconfirm
                title="Are you sure to delete this?"
                onConfirm={() => {
                  onDeleteConfirm(record);
                }}
                onCancel={() => {}}
                okText="Delete"
                cancelText="No"
                placement="rightTop"
                disabled={isEditable}
              >
                <Button
                  type="ghost"
                  size="small"
                  icon={<DeleteOutlined />}
                  title="Delete"
                  danger
                  disabled={isEditable}
                ></Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // const columnsForExport = [...columns];

  useEffect(() => {
    if (grid) {
      console.log('refetch for metaData with///', queryVariables);
      refetch(queryVariables);
    }
  }, [dispatch, refetch, grid, queryVariables]);

  const onChange = (updatedMetaData) => {
    console.log('onChange///', updatedMetaData);
    dispatch(
      updateTableMetaData({
        metaData: {
          ...metaData,
          ...updatedMetaData,
        },
      })
    );
    dispatch(
      updateGridMetaData({
        gridId,
        metaData: updatedMetaData,
      })
    );
  };

  const onPaymentSubmit = (values, callBack) => {
    console.log('onPaymentSubmit called with///', values);
    const isUpdate = values.id && values.id !== null;
    const variables = {
      id: isUpdate ? parseInt(values.id, 10) : values.employee.value,
      input: {
        for_month: values.for_month
          ? values.for_month.startOf('month').format(Config.DATE_FORMAT_SERVER)
          : null,
        amount: values.amount ? values.amount : '',
        note: values.note ? values.note : '',
      },
    };
    const mutationToCall = isUpdate ? updatePayment : addPayment;
    mutationToCall({
      variables,
    })
      .then(({ data }) => {
        const payment = isUpdate ? data.updatePayment : data.addPayment;
        console.log('onPaymentSubmit finished', payment);
        if (payment) {
          callBack({ status: true });
          onSuccessMutation();
          setShowAddEditPaymentModal(false);
        }
        setActivePayment(null);
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
        callBack({ status: false });
      });
  };

  const onDeleteConfirm = (payment) => {
    console.log('onDeleteConfirm called with///', payment);
    deletePayment({
      variables: {
        id: payment.id,
      },
    })
      .then(({ data: { deletePayment } }) => {
        console.log('onDeleteConfirm finished', deletePayment);
        if (deletePayment.id) {
          onSuccessMutation('Payment deleted!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onPaymentImportSubmit = (values, callBack) => {
    console.log('onPaymentImportSubmit called with///', values);
    const variables = {
      input: {
        file: {
          name: fileToImport.name,
          mime: fileToImport.mime,
          path: fileToImport.path,
        },
        for_month: values.for_month
          ? values.for_month.startOf('month').format(Config.DATE_FORMAT_SERVER)
          : null,
      },
    };
    importPayments({
      variables,
    })
      .then(({ data }) => {
        const importPayments = data.importPayments;
        console.log('onPaymentImportSubmit finished', importPayments);
        if (importPayments) {
          callBack({ status: true });
          onSuccessMutation();
          setShowAddPaymentImportModal(false);
          setFileToImport(null);
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = '';
        if (e.graphQLErrors) {
          e.graphQLErrors.forEach(({ extensions, message: errorMessage }) => {
            const { category, reason } = extensions;
            if (category === 'custom' && reason === 'Validation') {
              setImportErrors(errorMessage);
              // setShowAddIncentiveImportErrorModal(true);
            } else {
              error = errorMessage;
              message.error(
                error ? error : 'Some error occured, Please try again !!!'
              );
            }
          });
        }
      });
  };

  const onAddEditModalClose = () => {
    setShowAddEditPaymentModal(false);
    setActivePayment(null);
  };

  console.log('fetching ///2', fetching);

  const onImportModalClose = () => {
    setShowAddPaymentImportModal(false);
    setFileToImport(null);
  };

  return (
    <Spin spinning={loading || adding || updating || deleting || importing}>
      <Card className="employeesCard" style={{ minHeight: '200px' }}>
        {/* {error && <Error message={error.message} />} */}

        {data && (
          <DataGrid
            dataSource={data.payments.data}
            columns={columns}
            rowSelection={rowSelection}
            filters={grid.metaData.filters}
            visibleColumns={grid.metaData.visibleColumns}
            sorting={grid.metaData.sorting}
            pagination={grid.metaData.pagination}
            onChange={onChange}
            totalRecords={data.payments.paginatorInfo.total}
            onClearFilter={() => {
              dispatch(resetGridFilters({ gridId }));
            }}
            onImportMultiplEntry={
              ability.toDeletePayment &&
              (() => {
                setShowAddPaymentImportModal(true);
              })
            }
            onImportMultiplEntryBtnLabel="Import Payments"
            onAddNewEntry={
              ability.toDeletePayment &&
              (() => {
                setShowAddEditPaymentModal(true);
              })
            }
            addNewEntryBtnLabel="Add Payment"
          />
        )}
      </Card>
      <Modal
        title={'Import Payments'}
        visible={showAddPaymentImportModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={onImportModalClose}
        maskClosable={false}
      >
        <Spin spinning={importing}>
          <AddPaymentImportForm
            onPaymentImportSubmit={onPaymentImportSubmit}
            onCancelClick={onImportModalClose}
            UploadFile={
              <UploadFile
                onUploadSuccess={(upload) => {
                  console.log('upload file done with///', upload);
                  setFileToImport(upload);
                }}
                onFileRemoved={(files) => {
                  console.log('upload file removed///', files);
                  setFileToImport(null);
                }}
                mediaType="application/vnd.ms-excel, .xlsx, .csv"
                buttonLabel="Select file to import"
              />
            }
            importErrors={importErrors}
            ability={ability}
          />
        </Spin>
      </Modal>
      <Modal
        title={activePayment ? 'Edit Payment' : 'Add Payment'}
        visible={showAddEditPaymentModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={onAddEditModalClose}
        maskClosable={false}
      >
        <Spin spinning={adding || updating}>
          <AddPaymentForm
            payment={activePayment}
            lookupData={
              lookupData && lookupData.employeeLookup
                ? lookupData.employeeLookup.map((i) => {
                    return {
                      label: `${i.first_name} ${i.last_name} (${i.emp_id})`,
                      value: i.id,
                    };
                  })
                : []
            }
            onLookup={onEmployeeLookup}
            onPaymentSubmit={onPaymentSubmit}
            onCancelClick={onAddEditModalClose}
            fetching={fetching}
            ability={ability}
          />
        </Spin>
      </Modal>
    </Spin>
  );
};

export default Payments;
